import React from 'react';
import { CalendarClock, PieChart, Scale } from 'lucide-react';
import { FilingFact, FilingPanel } from './FilingPanel';

const QUOTA_BAR = {
  QIB: 'bg-indigo-500',
  NII: 'bg-amber-500',
  Retail: 'bg-[#008080]',
};

/** Issue structure, reservation split and any bidding dates the draft states. */
export const FilingTerms = ({ terms, quota, quotaNote, biddingDates }) => {
  if (!terms.length && !quota.length && !biddingDates.length) return null;

  return (
    <div className="space-y-4 sm:space-y-5">
      {terms.length > 0 && (
        <FilingPanel
          id="filing-terms-heading"
          title="Issue Structure"
          icon={Scale}
          note={
            terms.some((row) => row.unitUnknown)
              ? 'Amounts marked “unit as filed” are recorded without the unit the DRHP states them in (lakhs, million or crore). Check the offer document before relying on the figure.'
              : null
          }
        >
          <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4">
            {terms.map((row) => (
              <FilingFact key={row.label} label={row.label} value={row.value} hint={row.unitUnknown ? 'unit as filed' : null} />
            ))}
          </dl>
        </FilingPanel>
      )}

      {quota.length > 0 && (
        <FilingPanel id="filing-quota-heading" title="Reservation Structure" icon={PieChart} note={quotaNote}>
          <ul className="space-y-3">
            {quota.map((row) => {
              const percent = Math.min(100, Number(String(row.value).replace(/[^\d.]/g, '')) || 0);
              return (
                <li key={row.label}>
                  <div className="flex items-baseline justify-between gap-3 text-xs">
                    <span className="font-bold text-slate-700">{row.label}</span>
                    <span className="text-slate-500">
                      <span className="font-extrabold text-slate-900 tabular-nums">{percent}%</span>{' '}
                      <span className="text-[11px]">{row.qualifier}</span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full rounded-full ${QUOTA_BAR[row.label] || 'bg-slate-400'}`} style={{ width: `${percent}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </FilingPanel>
      )}

      {biddingDates.length > 0 && (
        <FilingPanel id="filing-dates-heading" title="Bidding Calendar" icon={CalendarClock}>
          <ol className="space-y-3">
            {biddingDates.map((entry) => (
              <li key={entry.label} className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-[#008080] flex-shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{entry.label}</p>
                  <p className="text-sm font-bold text-slate-900">{entry.value}</p>
                </div>
              </li>
            ))}
          </ol>
        </FilingPanel>
      )}
    </div>
  );
};

export default FilingTerms;
